import { useCallback, useContext, useState } from 'react';
import { SplitEvaluationContextV2 } from './context';
import { EvaluationScheduleFormType } from 'domains/signupDirect/schemas/evaluationScheduleSchema';
import { Outlet, useNavigate } from 'react-router-dom';
import { useSignupDirectCtx } from 'domains/signupDirect/context';
import { useDisplayTimezone } from 'domains/timezone/useDisplayTimezone';
import { routes } from 'router/routes';
import {
  EvaluationTimeSlot,
  formTimeSlotID,
  SlotAvailability,
} from 'domains/calendars/v3/useEvaluationCalendarSummary';
import { SmallTherapist } from 'domains/calendars/types';
import { SignupError } from 'utils/errors/debuggableError';

/**
 * Split evaluations - Schedule by time slot experiment
 */

export const SplitEvaluationsScheduleSectionV2 = () => {
  const { onContinueToEvalForm, evaluationSchedule } = useSignupDirectCtx();
  const [firstTimeSlotRange, setFirstTimeSlotRange] = useState<SlotAvailability | undefined>(
    getDefaultSlotRange('first', evaluationSchedule),
  );
  const [secondTimeSlotRange, setSecondTimeSlotRange] = useState<SlotAvailability | undefined>(
    getDefaultSlotRange('second', evaluationSchedule),
  );
  const [commonTherapists, setCommonTherapists] = useState<SmallTherapist[] | undefined>(
    getDefaultCommonTherapists(evaluationSchedule),
  );

  const { displayTimezone } = useDisplayTimezone();
  const navigate = useNavigate();

  const onSubmitFirstSlot = useCallback((timeSlot: SlotAvailability) => {
    setFirstTimeSlotRange(timeSlot);
    navigate(routes.signupDirect.evaluation.schedule.split.v2.second);
  }, []);

  const onSubmitSecondSlot = useCallback(
    (secondTimeSlot: SlotAvailability) => {
      if (!firstTimeSlotRange) throw new SignupError('Expected first time slot to be set');
      if (!displayTimezone) throw new SignupError('Expected display timezone to be set');

      setSecondTimeSlotRange(secondTimeSlot);

      const commonTherapists = findCommonTherapists(firstTimeSlotRange, secondTimeSlot);
      setCommonTherapists(commonTherapists);

      navigate(routes.signupDirect.evaluation.schedule.split.v2.therapist);
    },
    [firstTimeSlotRange, displayTimezone],
  );

  const onSkipSecondSlot = useCallback(() => {
    if (!firstTimeSlotRange) throw new SignupError('Expected first time slot to be set');
    if (!displayTimezone) throw new SignupError('Expected display timezone to be set');

    setSecondTimeSlotRange(undefined);

    const commonTherapists = findCommonTherapists(firstTimeSlotRange);

    setCommonTherapists(commonTherapists);
    navigate(routes.signupDirect.evaluation.schedule.split.v2.therapist);
  }, [firstTimeSlotRange, displayTimezone]);

  const onSubmitTherapist = useCallback(
    (therapist: SmallTherapist) => {
      if (!firstTimeSlotRange) throw new SignupError('Expected first time slot to be set');
      if (!displayTimezone) throw new SignupError('Expected display timezone to be set');

      // @TODO: use discriminated union in result to be saved onto signup context - useful later for coming back to edit, etc
      onContinueToEvalForm({
        type: 'split-by-slot',
        firstTimeSlotRange: firstTimeSlotRange,
        secondTimeSlotRange: secondTimeSlotRange,

        firstTimeSlot: getTimeSlotFromRange(firstTimeSlotRange, therapist),
        secondTimeSlot: secondTimeSlotRange ? getTimeSlotFromRange(secondTimeSlotRange, therapist) : undefined,

        timeZone: displayTimezone.resolvedName,
      });
    },
    [firstTimeSlotRange, secondTimeSlotRange, displayTimezone],
  );

  return (
    <SplitEvaluationContextV2.Provider
      value={{
        onSubmitFirstSlot,
        onSubmitSecondSlot,
        firstTimeSlot: firstTimeSlotRange,
        secondTimeSlot: secondTimeSlotRange,
        onSkipSecondSlot,
        onSubmitTherapist,
        commonTherapists,
      }}
    >
      <Outlet />
    </SplitEvaluationContextV2.Provider>
  );
};

const getDefaultSlotRange = (
  slot: 'first' | 'second',
  evaluationSchedule?: EvaluationScheduleFormType,
): SlotAvailability | undefined => {
  if (evaluationSchedule?.type === 'split-by-slot') {
    return evaluationSchedule[slot === 'first' ? 'firstTimeSlotRange' : 'secondTimeSlotRange'];
  }
  return undefined;
};

const getDefaultCommonTherapists = (evaluationSchedule?: EvaluationScheduleFormType): SmallTherapist[] | undefined => {
  if (evaluationSchedule?.type === 'split-by-slot') {
    return findCommonTherapists(evaluationSchedule.firstTimeSlotRange, evaluationSchedule.secondTimeSlotRange);
  }
  return undefined;
};

const findCommonTherapists = (firstTimeSlot: SlotAvailability, secondTimeSlot?: SlotAvailability): SmallTherapist[] => {
  const firstTherapists = firstTimeSlot.therapists;
  if (!secondTimeSlot) return firstTherapists;

  const secondTherapists = secondTimeSlot.therapists;

  const result = firstTherapists.filter(firstTherapist =>
    secondTherapists.some(secondTherapist => secondTherapist.therapistID === firstTherapist.therapistID),
  );

  if (result.length === 0)
    throw new SignupError('Expected at least one common therapist to be found', {
      debugData: { firstTherapists, secondTherapists },
    });

  return result;
};

export const useSplitEvaluationCtxV2 = () => {
  const ctx = useContext(SplitEvaluationContextV2);
  if (!ctx) throw new SignupError('Expected SplitEvaluationContext to be set');
  return ctx;
};

const getTimeSlotFromRange = (range: SlotAvailability, pickedTherapist: SmallTherapist): EvaluationTimeSlot => {
  if (!range.therapists.some(t => t.therapistID === pickedTherapist.therapistID))
    throw new SignupError('Expected picked therapist to be in range', {
      debugData: { range, pickedTherapist },
    });

  return {
    calendarID: pickedTherapist.acuityCalendarID,
    therapistID: pickedTherapist.therapistID,
    id: formTimeSlotID(range.time, pickedTherapist.therapistID),
    therapistName: `${pickedTherapist.firstName} ${pickedTherapist.lastName}`,
    time: range.time,
  };
};
